import React, { useContext, useEffect, useRef, useState } from 'react';
import { PropTypes } from 'prop-types';
import { Alert, Checkbox, Divider, List, TextInput } from '@mantine/core';
import { AlertCircle, InfoCircle } from 'tabler-icons-react';
import NumberFormat from 'react-number-format';
import { Context as RegistrationAdminContext } from '../../../providers/RegistrationAdminProvider';
import RegistrationListItem from './RegistrationListItem';
import { triggerNotification } from '../../../helpers/notificationHelper';
import { currencyFormat } from '../../../helpers/format';
import RefundTransactionOverview from './RefundTransactionOverview';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppTitle from '../../common/AppTitle';
import FormSection from '../../common/FormSection';
import AppStack from '../../common/AppStack';
import AppText from '../../common/AppText';
import AppCard from '../../common/AppCard';

const RegistrationRefundModal = ({
  regAssociation,
  isOpen,
  onClose,
  onRefresh,
  regFormSubmission
}) => {
  const hasInitialized = useRef(false);
  const {
    state,
    refundFinancialRegistration,
    fetchAdminRegFormSubmissionPaymentTransactionDetails
  } = useContext(RegistrationAdminContext);
  const [formState, setFormState] = useState({
    unlockRefundAmount: false,
    partialRefundAmount: 0,
    partialRefund: false,
    deactivateRegPerson: true,
    skipWaitlist: false,
    loading: false,
    showConfirm: false,
    showRefundWithoutTransferConfirmation: false,
    refundWithoutTransfer: false,
    refundWithoutPlatformFee: false
  });
  const transactionDetails =
    state.regFormSubmissionPaymentTransactionDetails.value;
  const lineItem = transactionDetails?.cart.lineItems.find(
    (i) => i.entityId === regFormSubmission?.pkRegFormSubmission
  );

  const hasInstallments = transactionDetails?.cart.payments.some(
    (p) => p.installments.length > 0
  );

  const isLoading =
    !regFormSubmission ||
    !hasInitialized.current ||
    !lineItem ||
    state.regFormSubmissionPaymentTransactionDetails.loading;
  const hasRefundableInvoices =
    lineItem && lineItem.invoices.some((i) => !i.isRefunded);
  const serviceFeeInCents = hasInstallments
    ? transactionDetails.cart.serviceFees.reduce(
        (r, f) => r + f.serviceFeeAmount,
        0
      ) / transactionDetails.cart.lineItems.length
    : lineItem && lineItem.amountPaidInCents > 0
    ? transactionDetails.paymentProviderTransaction &&
      transactionDetails.cart.lineItems.length === 1
      ? transactionDetails.paymentProviderTransaction.serviceFeeInCents
      : lineItem.refundServiceFee
    : 0;

  const groupCoupon = lineItem
    ? lineItem.coupons.find((c) => c.isGroupCoupon)
    : null;
  const lineItemsUsingGroupCoupon =
    transactionDetails?.cart.lineItems.filter(
      (i) =>
        i.entityId !== lineItem?.entityId &&
        i.coupons.some((c) => c.entityId === groupCoupon?.entityId)
    ) || [];
  const totalCouponAmountSavedInCents = lineItemsUsingGroupCoupon
    .map((i) => i.coupons.find((c) => c.entityId === groupCoupon?.entityId))
    .reduce((r, c) => r + c.amountSavedInCents, 0);

  const previouslyQualifiedForGroupCoupon =
    groupCoupon &&
    lineItemsUsingGroupCoupon.length >= groupCoupon.minimumQualifiedAmount;

  const appliedServiceFeeInCents = formState.unlockRefundAmount
    ? 0
    : serviceFeeInCents;
  const maxRefundAmount = lineItem
    ? (lineItem.amountPaidInCents -
        lineItem.refundedAmountInCents -
        appliedServiceFeeInCents) /
      100
    : 0;

  const regPersonRemoved = formState.partialRefund
    ? formState.deactivateRegPerson ||
      formState.partialRefundAmount >= maxRefundAmount
    : true;

  const maxRefundAmountWithoutPlatformFee = lineItem
    ? maxRefundAmount - lineItem.platformFeeInCents / 100
    : 0;
  const currency = lineItem?.currency?.toUpperCase();
  const selectedRefundAmount = formState.partialRefund
    ? !formState.deactivateRegPerson
      ? Math.min(
          formState.partialRefundAmount,
          maxRefundAmountWithoutPlatformFee
        )
      : formState.partialRefundAmount
    : maxRefundAmount;

  const customerRefundAmount = transactionDetails
    ? Math.max(
        0,
        selectedRefundAmount -
          transactionDetails.cart.installmentsScheduledInCents / 100
      )
    : selectedRefundAmount;

  useEffect(() => {
    if (isOpen && regFormSubmission) {
      fetchAdminRegFormSubmissionPaymentTransactionDetails(
        regFormSubmission.pkRegFormSubmission,
        null,
        triggerNotification
      );
      setFormState({
        unlockRefundAmount: false,
        partialRefundAmount: 0,
        partialRefund: false,
        deactivateRegPerson: true,
        skipWaitlist: false,
        loading: false,
        showConfirm: false,
        showRefundWithoutTransferConfirmation: false,
        refundWithoutTransfer: false,
        refundWithoutPlatformFee: false
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (lineItem && !hasInitialized.current) {
      setFormState({
        unlockRefundAmount: false,
        partialRefundAmount: maxRefundAmount,
        partialRefund: false,
        deactivateRegPerson: true,
        skipWaitlist: false,
        loading: false,
        showConfirm: false,
        showRefundWithoutTransferConfirmation: false,
        refundWithoutTransfer: false,
        refundWithoutPlatformFee: false
      });
      hasInitialized.current = true;
    }
  }, [lineItem]);

  useEffect(() => {
    if (formState.partialRefundAmount > maxRefundAmount) {
      setFormState((c) => ({
        ...c,
        partialRefundAmount: maxRefundAmount
      }));
    }
  }, [maxRefundAmount]);

  return (
    <ResponsiveModal
      isLoading={isLoading}
      onClose={onClose}
      opened={isOpen}
      size={600}
      title={<AppTitle order={2}>Refund Registration</AppTitle>}
    >
      {!isLoading && (
        <AppStack style={{ gap: 20 }}>
          {formState.showConfirm ? (
            <FormSection
              cancelTitle="Back"
              hideDivider
              isLoading={formState.loading}
              isSubmitDisabled={
                formState.showRefundWithoutTransferConfirmation &&
                !formState.refundWithoutTransfer &&
                !formState.refundWithoutPlatformFee
              }
              onCancel={() =>
                setFormState({
                  ...formState,
                  showConfirm: false
                })
              }
              onSubmit={() => {
                setFormState({
                  ...formState,
                  loading: true
                });

                const partialRefundAmount =
                  !formState.deactivateRegPerson &&
                  formState.partialRefundAmount >
                    maxRefundAmountWithoutPlatformFee
                    ? maxRefundAmountWithoutPlatformFee
                    : formState.partialRefundAmount;

                refundFinancialRegistration(
                  regFormSubmission.pkRegFormSubmission,
                  {
                    skipWaitlist: formState.skipWaitlist,
                    unlockRefundAmount: formState.unlockRefundAmount,
                    partialRefundAmount:
                      formState.showRefundWithoutTransferConfirmation &&
                      formState.refundWithoutPlatformFee
                        ? Number(maxRefundAmountWithoutPlatformFee).toFixed(2)
                        : formState.partialRefund &&
                          !!formState.partialRefundAmount
                        ? Number(partialRefundAmount).toFixed(2)
                        : null,
                    deactivateRegPerson: formState.partialRefund
                      ? formState.deactivateRegPerson
                      : false,
                    refundWithoutTransferReverse:
                      formState.showRefundWithoutTransferConfirmation &&
                      formState.refundWithoutTransfer
                  },
                  () => {
                    triggerNotification(
                      'Registration has been refunded',
                      'Success',
                      'green'
                    );
                    if (onRefresh) {
                      onRefresh();
                    }
                    onClose();
                  },
                  (error, errorType) => {
                    setFormState({
                      ...formState,
                      loading: false
                    });
                    if (errorType === 'old-transfer') {
                      setFormState({
                        ...formState,
                        isLoading: false,
                        showRefundWithoutTransferConfirmation: true,
                        refundWithoutTransfer: false
                      });
                    }
                    else if (errorType === 'reservations') {
                      triggerNotification(
                        'Registration has been refunded',
                        'Success',
                        'green'
                      );
                      if (onRefresh) {
                        onRefresh();
                      }

                      onClose();
                    }
                    else {
                      setFormState({
                        ...formState,
                        loading: false
                      });
                    }
                    triggerNotification(error);
                  }
                );
              }}
              submitColor="red"
              submitTitle={`Yes, refund ${currencyFormat(
                selectedRefundAmount
              )}`}
            >
              <AppStack style={{ gap: 20 }}>
                <AppCard
                  radius={8}
                  shadow="sm"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <RegistrationListItem
                    regAssociation={regAssociation}
                    regFormSubmission={regFormSubmission}
                  />
                </AppCard>

                <Divider />
                {formState.showRefundWithoutTransferConfirmation ? (
                  <Alert color="red" variant="outline">
                    <AppStack>
                      <AppText>
                        This submission has a platform fee transfer that is
                        greater than 30 days old and cannot be recovered. How
                        would you like to proceed?
                      </AppText>
                      <AppText style={{ fontWeight: 500 }}>
                        Please select one of the following options;
                      </AppText>

                      <AppStack style={{ gap: 5 }}>
                        <AppText style={{ fontWeight: 500, fontSize: 14 }}>
                          Option 1:
                        </AppText>
                        <Checkbox
                          checked={formState.refundWithoutTransfer}
                          label={`Refund full amount (you will cover the platform fee of ${currencyFormat(
                            lineItem.platformFeeInCents / 100
                          )} ${currency})`}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              refundWithoutTransfer: e.currentTarget.checked,
                              refundWithoutPlatformFee: false
                            })
                          }
                          style={{ fontWeight: 500 }}
                        />
                      </AppStack>

                      <AppStack style={{ gap: 5 }}>
                        <AppText style={{ fontWeight: 500, fontSize: 14 }}>
                          Option 2:
                        </AppText>
                        <Checkbox
                          checked={formState.refundWithoutPlatformFee}
                          label={`Refund total minus platform fee (${currencyFormat(
                            maxRefundAmountWithoutPlatformFee
                          )} ${currency})`}
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              refundWithoutPlatformFee: e.currentTarget.checked,
                              refundWithoutTransfer: false
                            })
                          }
                          style={{ fontWeight: 500 }}
                        />
                      </AppStack>
                    </AppStack>
                  </Alert>
                ) : (
                  <>
                    {formState.partialRefund &&
                      !formState.deactivateRegPerson &&
                      formState.partialRefundAmount >
                        maxRefundAmountWithoutPlatformFee && (
                        <Alert
                          color="yellow"
                          icon={<AlertCircle />}
                          styles={{ icon: { width: 28, height: 28 } }}
                          variant="outline"
                        >
                          <AppStack style={{ gap: 10 }}>
                            <AppText style={{ fontSize: 14 }} weight={500}>
                              Refund amount has been reduced by $3 to cover
                              registration service Fee while keeping the
                              registrant active. They will receive a refund of{' '}
                              <NumberFormat
                                decimalScale={2}
                                displayType="text"
                                fixedDecimalScale
                                prefix="$"
                                thousandSeparator
                                value={maxRefundAmountWithoutPlatformFee}
                              />{' '}
                              {currency}
                            </AppText>
                            <AppText style={{ fontSize: 14, color: 'grey' }}>
                              If you want to refund the full amount, please
                              deactivate the registrant from the division.
                            </AppText>
                          </AppStack>
                        </Alert>
                      )}

                    <AppStack style={{ gap: 16 }}>
                      <AppStack style={{ gap: 8 }}>
                        <AppText style={{ fontSize: 16 }}>
                          Are you sure you want to refund{' '}
                          <b>
                            {regFormSubmission.regPerson.firstName}{' '}
                            {regFormSubmission.regPerson.lastName}
                          </b>
                          ?
                        </AppText>

                        {transactionDetails?.cart.installmentsScheduledInCents >
                        0 ? (
                          <AppStack style={{ gap: 8 }}>
                            <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                              Total refund:{' '}
                              {currencyFormat(selectedRefundAmount)}
                            </AppText>

                            <List withPadding>
                              <List.Item>
                                <AppText style={{ fontSize: 14 }}>
                                  Scheduled installments cancelled:{' '}
                                  {currencyFormat(
                                    Math.min(
                                      selectedRefundAmount,
                                      transactionDetails.cart
                                        .installmentsScheduledInCents / 100
                                    )
                                  )}{' '}
                                  of{' '}
                                  {currencyFormat(
                                    transactionDetails.cart
                                      .installmentsScheduledInCents / 100
                                  )}
                                </AppText>
                              </List.Item>
                              <List.Item>
                                <AppText style={{ fontSize: 14 }}>
                                  Refund to customer:{' '}
                                  {currencyFormat(customerRefundAmount)}
                                </AppText>
                              </List.Item>
                            </List>
                          </AppStack>
                        ) : (
                          <AppText style={{ fontSize: 16 }}>
                            The customer will receive a refund of{' '}
                            <b>{currencyFormat(selectedRefundAmount)}</b>.
                          </AppText>
                        )}

                        <AppText
                          style={{
                            fontSize: 16,
                            fontWeight: 500,
                            color: '#C40000'
                          }}
                        >
                          This action cannot be undone.
                        </AppText>
                      </AppStack>

                      <AppText
                        style={{
                          fontSize: 14,
                          textAlign: 'center',
                          fontStyle: 'italic'
                        }}
                      >
                        {!regPersonRemoved
                          ? 'This registrant will stay in the division.'
                          : 'This registrant will be removed from the division.'}
                      </AppText>

                      {regPersonRemoved && (
                        <Alert
                          color="yellow"
                          icon={<AlertCircle />}
                          styles={{ icon: { width: 28, height: 28 } }}
                          title="Warning"
                          variant="outline"
                        >
                          <AppStack style={{ gap: 8 }}>
                            <AppText style={{ fontSize: 14 }}>
                              This action will <b>deactivate the registrant</b>{' '}
                              within the division and remove them from all
                              active teams associated with it.
                            </AppText>

                            <AppText style={{ fontSize: 14 }}>
                              You will not be able to reinstate this registrant
                              within the division.
                            </AppText>
                            <AppText style={{ fontSize: 14 }}>
                              They will have to re-register if they want to
                              participate in this division again.
                            </AppText>
                          </AppStack>
                        </Alert>
                      )}

                      {formState.unlockRefundAmount &&
                        selectedRefundAmount >
                          maxRefundAmount - serviceFeeInCents / 100 && (
                          <Alert
                            color="red"
                            icon={<AlertCircle />}
                            styles={{ icon: { width: 28, height: 28 } }}
                            title="Warning"
                            variant="outline"
                          >
                            <AppStack style={{ gap: 8 }}>
                              <AppText style={{ fontSize: 14 }}>
                                You are refunding at a loss of{' '}
                                <b>
                                  <NumberFormat
                                    decimalScale={2}
                                    displayType="text"
                                    fixedDecimalScale
                                    prefix="$"
                                    thousandSeparator
                                    value={
                                      selectedRefundAmount -
                                      (maxRefundAmount -
                                        serviceFeeInCents / 100)
                                    }
                                  />{' '}
                                  {currency}
                                </b>
                                .
                              </AppText>
                            </AppStack>
                          </Alert>
                        )}
                    </AppStack>
                  </>
                )}

                <Divider />
              </AppStack>
            </FormSection>
          ) : (
            <FormSection
              isLoading={formState.loading}
              onCancel={onClose}
              onSubmit={() =>
                setFormState({
                  ...formState,
                  showConfirm: true
                })
              }
              submitColor="blue"
              submitTitle="Continue to preview"
            >
              <AppStack style={{ gap: 20 }}>
                {transactionDetails.paymentProviderTransaction && (
                  <RefundTransactionOverview
                    amountRefunded={lineItem.refundedAmountInCents / 100}
                    currency={currency}
                    installmentsScheduled={
                      transactionDetails.cart.installmentsScheduledInCents / 100
                    }
                    serviceFee={serviceFeeInCents / 100}
                    transactionTotal={lineItem.amountPaidInCents / 100}
                    unlockRefundAmount={formState.unlockRefundAmount}
                  />
                )}
                <AppCard
                  radius={8}
                  shadow="sm"
                  style={{ padding: 0 }}
                  withBorder
                >
                  <RegistrationListItem
                    regAssociation={regAssociation}
                    regFormSubmission={regFormSubmission}
                  />
                </AppCard>

                <Divider label="REFUND OPTIONS" labelPosition="center" />

                {maxRefundAmount > 0 && (
                  <>
                    <Checkbox
                      checked={formState.unlockRefundAmount}
                      description="Enable a full refund by covering service fees at your own expense."
                      disabled={formState.loading}
                      label="Unlock refund amount"
                      onChange={() => {
                        setFormState({
                          ...formState,
                          unlockRefundAmount: !formState.unlockRefundAmount
                        });
                      }}
                      styles={{ label: { fontWeight: 500 } }}
                    />

                    <Checkbox
                      checked={formState.partialRefund}
                      description="Refund only part of the original payment amount."
                      disabled={formState.loading}
                      label="Partial Refund"
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          partialRefund: e.currentTarget.checked,
                          partialRefundAmount: maxRefundAmount
                        });
                      }}
                      styles={{ label: { fontWeight: 500 } }}
                    />
                    {formState.partialRefund && (
                      <>
                        <AppStack style={{ gap: 0 }}>
                          <NumberFormat
                            allowNegative={false}
                            customInput={TextInput}
                            decimalScale={2}
                            disabled={formState.loading}
                            fixedDecimalScale
                            label="Refund Amount"
                            name="refundAmount"
                            onValueChange={(values) =>
                              setFormState({
                                ...formState,
                                partialRefundAmount:
                                  values.floatValue > maxRefundAmount
                                    ? maxRefundAmount
                                    : values.floatValue
                              })
                            }
                            placeholder={`${currencyFormat(maxRefundAmount)}`}
                            prefix="$"
                            rightSection={
                              <AppText
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500,
                                  color: '#999',
                                  paddingRight: 5
                                }}
                              >
                                {currency}
                              </AppText>
                            }
                            style={{ flex: 1 }}
                            thousandSeparator
                            value={formState.partialRefundAmount}
                          />

                          <AppText style={{ fontSize: 14, color: '#666' }}>
                            {currencyFormat(maxRefundAmount)} available for
                            refund
                          </AppText>
                        </AppStack>

                        <Divider label="ACTIONS" labelPosition="center" />

                        <Checkbox
                          checked={formState.deactivateRegPerson}
                          description="This action will deactivate the registrant within the division and remove them from all active teams associated with it."
                          disabled={formState.loading}
                          label="Remove registrant from division"
                          onChange={(e) =>
                            setFormState({
                              ...formState,
                              deactivateRegPerson: e.currentTarget.checked,
                              partialRefundAmount:
                                formState.partialRefundAmount > maxRefundAmount
                                  ? maxRefundAmount
                                  : formState.partialRefundAmount
                            })
                          }
                          styles={{ label: { fontWeight: 500 } }}
                        />

                        {formState.deactivateRegPerson && (
                          <Alert
                            color="yellow"
                            icon={<AlertCircle />}
                            styles={{ icon: { width: 28, height: 28 } }}
                            title="Warning"
                            variant="outline"
                          >
                            <AppStack style={{ gap: 8 }}>
                              <AppText
                                style={{
                                  fontSize: 14,
                                  fontWeight: 500
                                }}
                              >
                                You will not be able to reinstate this
                                registrant within the division.
                              </AppText>
                              <AppText style={{ fontSize: 14 }}>
                                They will have to re-register if they want to
                                participate in this division again.
                              </AppText>
                            </AppStack>
                          </Alert>
                        )}
                      </>
                    )}
                  </>
                )}

                {hasRefundableInvoices && (
                  <Alert
                    color="yellow"
                    icon={<AlertCircle />}
                    styles={{ icon: { width: 28, height: 28 } }}
                    variant="outline"
                  >
                    <AppText style={{ fontSize: 14 }} weight={500}>
                      {regFormSubmission.paidRegFormSubmissionInvoiceCount}{' '}
                      Invoice(s) will have to be refunded separately
                    </AppText>
                  </Alert>
                )}

                {regFormSubmission.regAssociationDivisionForm
                  .regAssociationDivision.waitlistEnabled && (
                  <>
                    <Divider />
                    <Checkbox
                      checked={!formState.skipWaitlist}
                      label="Notify waitlist registrant"
                      onChange={(e) => {
                        setFormState({
                          ...formState,
                          skipWaitlist: !e.currentTarget.checked
                        });
                      }}
                      styles={{
                        label: {
                          fontSize: 14,
                          fontWeight: 500
                        }
                      }}
                    />
                    <Alert
                      color={formState.skipWaitlist ? 'yellow' : 'blue'}
                      icon={
                        formState.skipWaitlist ? (
                          <AlertCircle style={{ width: 35, height: 35 }} />
                        ) : (
                          <InfoCircle style={{ width: 35, height: 35 }} />
                        )
                      }
                      styles={{ icon: { marginTop: 15, width: 35 } }}
                      variant="outline"
                    >
                      {formState.skipWaitlist ? (
                        <AppText>
                          The next waitlist registrant will not be notified of
                          any open positions.
                        </AppText>
                      ) : (
                        <AppText>
                          We will notify the next waitlist registrant that they
                          can now register for{' '}
                          {
                            regFormSubmission.regAssociationDivisionForm
                              .regAssociationDivision.name
                          }
                          .
                        </AppText>
                      )}
                    </Alert>
                  </>
                )}
              </AppStack>
            </FormSection>
          )}
        </AppStack>
      )}
    </ResponsiveModal>
  );
};

RegistrationRefundModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onRefresh: PropTypes.func,
  regAssociation: PropTypes.object,
  regFormSubmission: PropTypes.object
};
export default RegistrationRefundModal;
